import { useState } from 'react'
import { Box, Stack, styled } from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import { DateRange } from '@mui/x-date-pickers-pro'
import { Button, DateRangePicker as DeckDateRangePicker, DatePickerTextInputSize, theme } from '@ketch-com/deck'
import { useFiltersV3 } from 'utils/hooks'

const classes = {
  selectedDateRangeButton: 'ReportsDateRangePicker-selectedDateRangeButton',
  datePickerButton: 'ReportsDateRangePicker-selectedDateRangeButton',
}

const StyledBox = styled(Box)(() => ({
  [`& .${classes.selectedDateRangeButton}`]: {
    outline: `1px solid ${theme.palette.Action.Primary}`,
  },
  [`& .${classes.datePickerButton}`]: {
    border: 'none',
  },
}))

const StyledDatePickerActionBar = styled(Box)(() => ({
  gridRow: 3,
  minWidth: '650px',
  gridColumn: 2,
  margin: '0 8px 8px 8px',
  '& .StyledDatePickerActionBar-container': {
    borderTop: `1px solid ${theme.palette.Common.Divider}`,
    padding: '8px 0',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 8,
  },
}))

const currentDate = new Date()
const QUICK_RANGES = [7, 30, 90]

const DateRangePicker = () => {
  const { replaceParams } = useFiltersV3()

  const [lastAppliedRange, setLastAppliedRange] = useState<DateRange<Dayjs>>([null, null])
  const [range, setRange] = useState<DateRange<Dayjs>>(lastAppliedRange)
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const startDate = range[0] || dayjs(currentDate)
  const endDate = range[1] || dayjs(currentDate)
  const selectedRangeDiffInDays = startDate.diff(endDate, 'day')

  const handleDateRangeChange = (newValue?: DateRange<Dayjs>) => {
    if (!newValue) return

    const [start, end] = newValue
    if (dayjs(start).isValid() && dayjs(end).isValid()) {
      setRange(newValue)
    }
  }

  const handleClearClick = () => {
    replaceParams({ invokedAtStart: '', invokedAtEnd: '' })
    setRange([null, null])
    setLastAppliedRange([null, null])
    setIsOpen(false)
  }

  const handleApplyClick = () => {
    replaceParams({
      invokedAtStart: range[0]?.format('YYYY-MM-DD') || '',
      invokedAtEnd: range[1]?.format('YYYY-MM-DD') || '',
    })
    setLastAppliedRange(range)
    setIsOpen(false)
  }

  const handleClose = () => {
    const changed = range[0]?.isSame(lastAppliedRange[0]) === false || range[1]?.isSame(lastAppliedRange[1]) === false
    if (changed) {
      setRange([null, null])
    } else {
      setRange(lastAppliedRange)
    }

    setIsOpen(false)
  }

  return (
    <StyledBox>
      <DeckDateRangePicker
        textInputSize={'small' as DatePickerTextInputSize}
        label=""
        open={isOpen}
        value={range}
        onOpen={() => setIsOpen(true)}
        onClose={handleClose}
        disableFuture
        minDate={dayjs().subtract(1, 'year').startOf('year')}
        onChange={handleDateRangeChange}
        slots={{
          actionBar: () => (
            <StyledDatePickerActionBar>
              <Box className={'StyledDatePickerActionBar-container'}>
                <Stack direction="row" justifyContent="space-between" style={{ width: '100%' }}>
                  <Stack direction="row" spacing={1}>
                    {QUICK_RANGES.map(days => (
                      <Button
                        key={days}
                        color="tertiary"
                        onClick={e => {
                          e.preventDefault()
                          setRange([dayjs(currentDate).add(-days, 'day'), dayjs(currentDate)])
                        }}
                        sx={{
                          outline:
                            selectedRangeDiffInDays === -days ? `1px solid ${theme.palette.Action.Primary}` : 'none',
                        }}
                      >
                        Last {days} Days
                      </Button>
                    ))}
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Button variant="text" onClick={handleClearClick} sx={{ color: theme.palette.Action.Primary }}>
                      Clear
                    </Button>
                    <Button color="secondary" onClick={handleClose}>
                      Cancel
                    </Button>
                    <Button disabled={!range[0] || !range[1]} color="primary" onClick={handleApplyClick}>
                      Apply
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </StyledDatePickerActionBar>
          ),
        }}
      />
    </StyledBox>
  )
}

export default DateRangePicker
